import React from "react";
import { useSelector } from "react-redux";
import { FaRupeeSign } from "react-icons/fa";
import styles from "./PaymentModesDistribution.module.css";

/* Keep segment widths safe */
const clamp = (n) => Math.max(0, Math.min(100, Number(n) || 0));

/* Default palette applied by index */
const DEFAULT_GRADIENTS = [
  "linear-gradient(90deg, #2f8fe0 0%, #38bdf8 100%)",
  "linear-gradient(90deg, #7c3aed 0%, #a78bfa 100%)",
  "linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%)",
  "linear-gradient(90deg, #10b981 0%, #34d399 100%)",
];

const PaymentModesDistribution = ({
  title,
  modes = [],
  loading = false,
  error = null,
  emptyMessage = "No payment mode data available",
}) => {
  const { translations } = useSelector((state) => state.language);
  const t = translations?.labels || {};

  const headerTitle =
    title || t.paymentModesDistribution || "Payment Modes Distribution";

  const renderBody = () => {
    if (loading) return <div className={styles.stateBox}>Loading…</div>;
    if (error)
      return (
        <div className={`${styles.stateBox} ${styles.stateError}`}>
          {String(error)}
        </div>
      );
    if (!Array.isArray(modes) || modes.length === 0)
      return <div className={styles.stateBox}>{emptyMessage}</div>;

    const totalPercent = modes.reduce(
      (sum, m) => sum + clamp(m.percent),
      0
    );
    const isAllZero = totalPercent === 0;

    return (
      <>
        {/* Labels Row */}
        <div className={styles.labelsRow}>
          {modes.map((mode) => {
            // Distribute widths equally if everything is 0% so labels don't stack
            const blockWidth = isAllZero
              ? 100 / modes.length
              : clamp(mode.percent);

            return (
              <div
                key={mode.key || mode.label}
                className={styles.labelBlock}
                style={{ width: `${blockWidth}%` }}
              >
                <span className={styles.modeLabel}>{mode.label}</span>
                <span className={styles.modePercent}>
                  {clamp(mode.percent)}%
                </span>
              </div>
            );
          })}
        </div>

        {/* Bar Row */}
        <div
          className={styles.bar}
          style={{
            backgroundColor: isAllZero ? "#E5E7EB" : "transparent",
            borderRadius: 8,
            height: 12,
            overflow: "hidden",
            display: "flex",
          }}
        >
          {!isAllZero &&
            modes.map((mode, i) => (
              <div
                key={mode.key || mode.label}
                className={styles.segment}
                style={{
                  width: `${clamp(mode.percent)}%`,
                  background:
                    mode.gradient ||
                    DEFAULT_GRADIENTS[i % DEFAULT_GRADIENTS.length],
                }}
                title={`${mode.label}: ${clamp(mode.percent)}%`}
              />
            ))}
        </div>
      </>
    );
  };

  return (
    <div className="w-100">
      <div className={styles.header}>
        <span className={styles.icon} aria-hidden="true">
          <FaRupeeSign />
        </span>
        <h3 className="chartTitle">{headerTitle}</h3>
      </div>

      {renderBody()}
    </div>
  );
};

export default PaymentModesDistribution;
